/**
 * Text Analysis Utilities
 * Functions for analyzing text responses, extracting word frequencies, etc.
 */

// Common stop words to filter out
const STOP_WORDS = new Set([
  'a', 'an', 'and', 'are', 'as', 'at', 'be', 'by', 'for', 'from',
  'has', 'he', 'in', 'is', 'it', 'its', 'of', 'on', 'or', 'that',
  'the', 'to', 'was', 'were', 'will', 'with', 'would', 'could', 'should',
  'i', 'me', 'my', 'myself', 'we', 'our', 'ours', 'ourselves', 'you', 'your',
  'yours', 'yourself', 'yourselves', 'she', 'her', 'hers', 'herself',
  'they', 'them', 'their', 'theirs', 'themselves', 'what', 'which', 'who',
  'whom', 'this', 'these', 'those', 'am', 'been', 'being', 'have', 'had',
  'having', 'do', 'does', 'did', 'doing', 'but', 'if', 'so', 'than',
  'too', 'very', 'can', 'just', 'dont', "don't", 'should', 'now',
  'also', 'more', 'about', 'all', 'any', 'both', 'each', 'few', 'how',
  'many', 'much', 'no', 'nor', 'not', 'only', 'other', 'some', 'such',
  'then', 'there', 'when', 'where', 'why', 'again', 'further', 'once',
  'here', 'because', 'until', 'while', 'during', 'before', 'after',
  'above', 'below', 'up', 'down', 'out', 'off', 'over', 'under',
  'same', 'into', 'through', 'against', 'between', 'without', 'within',
  // Additional common words
  'get', 'got', 'getting', 'like', 'think', 'know', 'make', 'made',
  'want', 'need', 'see', 'go', 'going', 'come', 'take', 'say', 'said',
  'one', 'two', 'first', 'last', 'new', 'old', 'good', 'great', 'best',
  'well', 'really', 'yes', 'yeah', 'ok', 'okay'
]);

export interface WordFrequency {
  text: string;
  value: number;
}

/**
 * Extract word frequencies from a collection of text responses
 * @param texts Array of text strings to analyze
 * @param options Configuration options
 * @returns Array of word frequencies sorted by count
 */
export function extractWordFrequencies(
  texts: string[],
  options: {
    minWordLength?: number;
    maxWords?: number;
    includeStopWords?: boolean;
    caseSensitive?: boolean;
  } = {}
): WordFrequency[] {
  const {
    minWordLength = 3,
    maxWords = 100,
    includeStopWords = false,
    caseSensitive = false
  } = options;
  
  const wordCounts = new Map<string, number>();
  
  texts.forEach(text => {
    if (!text || typeof text !== 'string') return;
    
    // Normalize and tokenize
    let normalizedText = text;
    if (!caseSensitive) {
      normalizedText = text.toLowerCase();
    }
    
    // Split on non-word characters and filter
    const words = normalizedText
      .replace(/[^\w\s'-]/g, ' ') // Remove punctuation except apostrophes and hyphens
      .split(/\s+/)
      .filter(word => {
        // Filter by length
        if (word.length < minWordLength) return false;
        
        // Filter stop words unless included
        if (!includeStopWords && STOP_WORDS.has(word.toLowerCase())) return false;
        
        // Filter numeric-only words
        if (/^\d+$/.test(word)) return false;
        
        return true;
      });
    
    // Count words
    words.forEach(word => {
      wordCounts.set(word, (wordCounts.get(word) || 0) + 1);
    });
  });
  
  // Convert to array and sort by count
  const sortedWords = Array.from(wordCounts.entries())
    .map(([text, value]) => ({ text, value }))
    .sort((a, b) => b.value - a.value)
    .slice(0, maxWords);
  
  return sortedWords;
}

/**
 * Helper to get answer value from response - handles both old and new data structures
 */
function getAnswerValue(response: any, questionId: string): any {
  // New structure: answers_json.questions[questionId]
  if (response.answers_json?.questions?.[questionId] !== undefined) {
    return response.answers_json.questions[questionId];
  }
  // Old structure: answers_json[questionId]
  if (response.answers_json?.[questionId] !== undefined) {
    return response.answers_json[questionId];
  }
  return undefined;
}

/**
 * Extract text responses from a specific question
 * @param responses Array of response objects
 * @param questionId The question ID to extract text from
 * @returns Array of text strings
 */
export function extractTextResponses(
  responses: any[],
  questionId: string
): string[] {
  return responses
    .map(r => getAnswerValue(r, questionId))
    .filter(answer => typeof answer === 'string' && answer.trim().length > 0) as string[];
}

/**
 * Check if a question type typically contains free-text responses
 */
export function isTextQuestion(type: string): boolean {
  const textTypes = ['text', 'textarea', 'long_text', 'paragraph', 'essay', 'comment', 'open'];
  return textTypes.includes(type.toLowerCase());
}
